import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import LockIcon from "@mui/icons-material/Lock"
import type { Ape } from "./useOwnedApes"
import collection from "./data/collection.json"
import apesUrl from "url:./data/apes.json"

const counts: Record<string, Record<string, number>> = collection.counts

// [dna, Arweave metadata tx] per ape number. Fetched (not bundled) so the 500 KB file only loads on pages that need it.
type ApeIndex = [dna: string, arweaveTx: string][]
let index: Promise<ApeIndex> | undefined
export const loadApeIndex = (): Promise<ApeIndex> => (index ??= fetch(apesUrl).then(r => {
	if (!r.ok) throw new Error(`Couldn't load the ape list (${r.status}).`)
	return r.json()
}))

// Ape metadata (image, traits) straight from Arweave, so it works for unminted apes too
export const fetchArweaveApe = async (number: number, dna: string, tx: string): Promise<Ape> => {
	const res = await fetch(`https://arweave.net/${tx}`)
	if (!res.ok) throw new Error(`Couldn't load the ape's metadata (Arweave ${res.status}).`)
	const m = await res.json()
	return { id: dna, number, name: m.name, image: m.image, attributes: m.attributes ?? [] }
}

// Square ape image; unminted apes stay blurred behind "Mint to reveal" (cosmetic: the URL is still in the page)
export const ApeImage = ({ ape, hidden }: { ape: Ape, hidden: boolean }) => (
	<Box sx={{ position: "relative", overflow: "hidden", lineHeight: 0 }}>
		<Box component="img" src={ape.image} alt={hidden ? "Undiscovered ape" : ape.name}
			sx={{ width: "100%", aspectRatio: "1", ...(hidden && { filter: "blur(22px) saturate(1.2)", transform: "scale(1.2)" }) }} />
		{hidden && (
			<Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#fff", textShadow: "0 1px 6px rgba(0,0,0,.35)", lineHeight: 1.5 }}>
				<LockIcon sx={{ fontSize: 40 }} />
				<Typography sx={{ fontWeight: 700, mt: 1 }}>Mint to reveal</Typography>
			</Box>
		)}
	</Box>
)

export const RankChip = ({ number }: { number: number }) => (
	<Chip color="secondary" size="small" label={`Rank ${collection.rank[number] ?? "?"} / ${collection.total}`} sx={{ fontWeight: 600 }} />
)

const pct = (type: string, value: string) => {
	const n = counts[type]?.[value]
	return n ? `${(n / collection.total * 100).toFixed(1)}%` : "?"
}

const ApeTraits = ({ attributes }: { attributes: Ape["attributes"] }) => (
	<Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 1 }}>
		{attributes.map(a => (
			<Box key={a.trait_type} sx={{ p: 1.25, borderRadius: 1, bgcolor: "rgba(79,168,156,.09)" }}>
				<Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: .5, fontWeight: 600 }}>
					{a.trait_type}
				</Typography>
				<Typography variant="body2" sx={{ fontWeight: 600 }}>{a.value}</Typography>
				<Typography variant="caption" color="primary.dark">{pct(a.trait_type, a.value)} have this</Typography>
			</Box>
		))}
	</Box>
)

export default ApeTraits
